
import React from "react";
import { Check } from "lucide-react";

interface TariffCardProps {
  tariff: {
    key: string;
    title: string;
    price: number;
    period: string;
    description: string;
    perks: string[];
  };
  active: boolean;
  onSelect: () => void;
}

export const TariffCard: React.FC<TariffCardProps> = ({ tariff, active, onSelect }) => (
  <div
    className={`
      group relative overflow-hidden
      w-full p-6 rounded-2xl
      ${active 
        ? "bg-gradient-to-br from-violet-800 to-violet-900 dark:from-violet-900 dark:to-violet-950" 
        : "bg-gradient-to-br from-slate-800/90 to-slate-900/90 dark:from-gray-800/90 dark:to-gray-900/90"
      }
      shadow-lg
      cursor-pointer select-none
      flex flex-col text-left min-h-[250px] sm:min-h-[280px]
      ring-2 ${active ? "ring-violet-400" : "ring-transparent"}
      focus:outline-none focus:ring-violet-500
      transition-all duration-300 ease-in-out
      hover:scale-[1.02] hover:shadow-xl
      hover:ring-2 hover:ring-violet-500/50
      backdrop-blur-sm
      before:absolute before:inset-0 
      before:bg-gradient-to-r before:from-transparent 
      before:via-white/5 before:to-transparent
      before:translate-x-[-200%]
      before:transition-transform before:duration-700
      group-hover:before:translate-x-[200%]
    `}
    onClick={onSelect}
    tabIndex={0}
    onKeyUp={(e) => {
      if (e.key === "Enter" || e.key === " ") onSelect();
    }}
    aria-pressed={active}
    aria-label={`Тариф ${tariff.title}`}
  >
    {active && (
      <div className="absolute z-20 top-4 right-4 text-white bg-violet-500 p-1.5 rounded-full drop-shadow-lg animate-fade-in">
        <Check size={18} strokeWidth={3} />
      </div>
    )}

    <div className="space-y-4">
      <div>
        <div className={`text-xl sm:text-2xl font-bold mb-1 leading-tight ${
          active ? "text-white" : "text-white"
        }`}>
          {tariff.title}
        </div>
        <div className={`text-3xl sm:text-4xl font-extrabold ${
          active ? "text-white" : "text-violet-300"
        }`}>
          {tariff.price.toLocaleString()}<span className="text-xl sm:text-2xl font-semibold">₽</span>{" "}
          <span className="text-base sm:text-lg font-light text-gray-300">/ {tariff.period}</span>
        </div>
      </div>

      {tariff.description && (
        <div
          className={`
            inline-block font-medium px-3 sm:px-4 py-1.5 rounded-xl select-none 
            ${active 
              ? "bg-violet-600/60 text-white" 
              : "bg-slate-700/60 text-gray-200"
            }
            backdrop-blur-sm
          `}
        >
          {tariff.description}
        </div>
      )}

      <ul className={`list-none space-y-2 mt-4 ${active ? "text-gray-100" : "text-gray-300"}`}>
        {tariff.perks.map((perk, i) => (
          <li key={i} className="flex items-center gap-2 text-sm sm:text-base leading-tight">
            <Check size={16} className={active ? "text-violet-300" : "text-violet-400"} />
            {perk}
          </li>
        ))}
      </ul>
    </div>

    <div className="flex-grow" />

    <div className={`
      mt-4 w-full h-1 rounded-full overflow-hidden
      ${active ? "bg-violet-600/30" : "bg-slate-700/30"}
    `}>
      <div className={`
        h-full w-full rounded-full
        ${active 
          ? "bg-gradient-to-r from-violet-400 to-violet-500" 
          : "bg-gradient-to-r from-violet-500/40 to-violet-600/40"
        }
        transition-transform duration-300
        ${active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}
      `} />
    </div>
  </div>
);
